import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { TIME_FILTER_TABS, TimeFilterTabs } from '@/utilities/etransportTypes';
import { useTranslation } from 'react-i18next';
import { SearchComponent } from '@/components/common/Search';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import SubscriptionsOutlinedIcon from '@mui/icons-material/SubscriptionsOutlined';
import { CategoryCountSelector } from '@/components/common/selection/CategoryCountSelector';
import { Dispatch, RefObject, SetStateAction, useContext, useState } from 'react';
import { PlutoCustomerDto } from '@/generated/model';
import { ctxUser } from '@/contexts/UserContext';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import './etransport.css';

export function FilterBar({
  customer,
  customers,
  isLoading,
  activeTab,
  categories,
  searchTarget,
  subscriptionButtonRef,
  setCustomer,
  setActiveTab,
  onSearchChange,
  onOpenSubscriptions,
}: {
  customer: PlutoCustomerDto | undefined;
  customers: PlutoCustomerDto[] | undefined;
  isLoading: boolean;
  activeTab: TimeFilterTabs;
  categories: Map<TimeFilterTabs, number>;
  searchTarget: string | undefined;
  subscriptionButtonRef: RefObject<HTMLButtonElement | null>;
  setCustomer: Dispatch<SetStateAction<PlutoCustomerDto | undefined>>;
  setActiveTab: (tab: TimeFilterTabs) => void;
  onSearchChange: (value: string | undefined) => void;
  onOpenSubscriptions: () => void;
}) {
  const { t } = useTranslation();
  const userCtx = useContext(ctxUser);
  const admin = userCtx?.user.isSuperAdmin;
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        alignItems: 'flex-start',
      }}
    >
      <Box width="100%" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
            <Typography variant="h6" sx={{ color: 'black' }}>
              {t('mainMenu.etransport')}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setInfoDialogOpen(true)}
              aria-label={t('etransport.filterbar.info')}
              data-qa-element-id="etr_transport_info"
            >
              <InfoIcon sx={{ fontSize: '1.5rem', color: 'text.secondary' }} />
            </IconButton>
          </Stack>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={t('etransport.subscriptions.open')} arrow>
              <IconButton
                ref={subscriptionButtonRef}
                size="small"
                onClick={onOpenSubscriptions}
                aria-label={t('etransport.subscriptions.open')}
                data-qa-element-id="etr_subscriptions_open"
              >
                <SubscriptionsOutlinedIcon sx={{ fontSize: '1.5rem', color: 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </>
      </Box>
      <Box width="100%">
        <TargetSearch searchTarget={searchTarget} onSearchChange={onSearchChange} />
      </Box>

      <Box
        width="100%"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: isPhoneOrHighScreen() ? '7px' : '0px',
        }}
      >
        <TimeFilter isLoading={isLoading} activeTab={activeTab} categories={categories} setActiveTab={setActiveTab} />
        {admin && (
          <Autocomplete
            options={customers ?? []}
            value={customer ?? null}
            getOptionLabel={(option) => option.name ?? ''}
            onChange={(_, newValue) => setCustomer(newValue ?? undefined)}
            onClose={(_, reason) => {
              if (reason === 'selectOption') (document.activeElement as HTMLElement)?.blur();
            }}
            sx={{ flex: isPhoneOrHighScreen() ? '0 1 150px' : '0 1 200px' }}
            slotProps={{ paper: { elevation: 4 } }}
            renderInput={(params) => (
              <TextField {...params} size="small" placeholder={t('tachograph.table.customer')} />
            )}
          />
        )}
      </Box>
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="etransport-info-dialog-title"
      >
        <DialogTitle id="etransport-info-dialog-title" component="h6">
          {t('mainMenu.etransport')}
        </DialogTitle>
        <IconButton
          aria-label={t('general.close')}
          onClick={() => setInfoDialogOpen(false)}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
          data-qa-element-id="etr_transport_info_close"
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Typography>{t('etransport.filterbar.info')}</Typography>
        </DialogContent>
        {admin && (
          <DialogContent>
            <Typography>{t('etransport.filterbar.adminInfo')}</Typography>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>{t('general.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function TargetSearch({
  searchTarget,
  onSearchChange,
}: {
  searchTarget: string | undefined;
  onSearchChange: (value: string | undefined) => void;
}) {
  const { t } = useTranslation();
  return (
    <SearchComponent
      searchText={searchTarget}
      setSearchText={onSearchChange}
      placeholder={t('etransport.filterbar.target.platesearch')}
      showHistory={true}
    />
  );
}

function TimeFilter({
  isLoading,
  activeTab,
  categories,
  setActiveTab,
}: {
  isLoading: boolean;
  activeTab: TimeFilterTabs;
  categories: Map<TimeFilterTabs, number>;
  setActiveTab: (tab: TimeFilterTabs) => void;
}) {
  const { t, i18n } = useTranslation();
  const DEFAULT_WIDTH: string = '175px';
  const badgeColors = new Map<TimeFilterTabs, string>([
    ['All', '#333333'],
    ['Inactive', '#bdbdbd'],
    ['Active', 'var(--mui-palette-secondary-main)'],
    ['Future', 'var(--mui-palette-primary-main)'],
  ]);

  function getLabel(label: TimeFilterTabs): string {
    return t(`etransport.filterbar.freight.${label.toLowerCase()}`);
  }

  //just add the exceptions here
  const minWidthByLocale: Record<string, string> = {
    ro: '210px',
    cz: '200px',
    sl: '190px',
    hr: '190px',
  };

  return (
    <CategoryCountSelector
      options={TIME_FILTER_TABS}
      minWidth={minWidthByLocale[i18n.language] ?? DEFAULT_WIDTH}
      isLoading={isLoading}
      activeTab={activeTab}
      badgeColors={badgeColors}
      numOfCategories={categories}
      getLabel={getLabel}
      setActiveTab={setActiveTab}
    />
  );
}
